import { filterBranchLocations } from './destinationType.js';

/**
 * Normalize destination_location_hub_ids from a hub DTO.
 */
export function getMappedDestinationHubIds(hub) {
  if (!hub) return [];
  const raw = hub.destination_location_hub_ids;
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id)).filter(Boolean);
  }
  return String(raw)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Resolve allowed destination hub PKs for a source hub (client-side mirror of backend).
 * - Empty mapping → null (no restriction)
 * - Branch with empty mapping inherits parent hub mapping
 * - Expands selected hubs to include child branches
 * - Always excludes the source hub itself
 */
export function resolveAllowedDestinationHubIds(sourceHub, allHubs = []) {
  if (!sourceHub) return null;

  const hubsById = {};
  (allHubs || []).forEach((hub) => {
    if (hub?.id != null) hubsById[String(hub.id)] = hub;
  });

  let mapped = getMappedDestinationHubIds(sourceHub);
  if (
    mapped.length === 0
    && String(sourceHub.destination_type || '').toLowerCase() === 'branch_destination'
    && sourceHub.parent_hub_id
  ) {
    const parent = hubsById[String(sourceHub.parent_hub_id)];
    mapped = getMappedDestinationHubIds(parent);
  }

  if (!mapped.length) return null;

  const allowed = new Set(mapped);
  // Expand hub destinations to include their child branches
  (allHubs || []).forEach((hub) => {
    if (hub?.parent_hub_id != null && allowed.has(String(hub.parent_hub_id))) {
      allowed.add(String(hub.id));
    }
  });

  const sourceId = String(sourceHub.id);
  allowed.delete(sourceId);
  return allowed;
}

/**
 * Filter To locations for booking based on source hub destination mapping.
 * - No mapping → keep current branch-only list
 * - Mapping set → mapped hubs + their child branches (minus source)
 */
export function toLocationsForBooking(allHubs, sourceHubId, { allLocations = null } = {}) {
  const pool = allLocations || allHubs || [];
  const hubs = allHubs || [];
  const sourceId = sourceHubId != null ? String(sourceHubId) : '';
  const sourceHub = sourceId
    ? hubs.find((h) => String(h.id) === sourceId)
    : null;

  const allowed = resolveAllowedDestinationHubIds(sourceHub, hubs);
  if (allowed == null) {
    return filterBranchLocations(pool);
  }

  return (pool || []).filter((hub) => allowed.has(String(hub.id)));
}

/**
 * Filter ML To locations by destination mapping on top of an existing To pool
 * (typically already filtered by branch + dispatch intersection).
 * When multiple From hubs are selected, union their allowed sets.
 * If any From has no mapping, that From contributes no restriction for its part —
 * overall: if ALL from hubs have no mapping → return pool unchanged;
 * if SOME have mapping → intersection of restrictions is too harsh; use union of
 * allowed ids from mapped sources, and if any source is unrestricted include full pool.
 */
export function filterToLocationsByDestinationMapping(toPool, fromLocationIds, allHubs = []) {
  const fromIds = (fromLocationIds || []).map(String).filter(Boolean);
  if (!fromIds.length) return toPool || [];

  const hubsById = {};
  (allHubs || []).forEach((hub) => {
    if (hub?.id != null) hubsById[String(hub.id)] = hub;
  });

  let unrestricted = false;
  const unionAllowed = new Set();

  for (const fromId of fromIds) {
    const sourceHub = hubsById[fromId];
    const allowed = resolveAllowedDestinationHubIds(sourceHub, allHubs);
    if (allowed == null) {
      unrestricted = true;
      break;
    }
    allowed.forEach((id) => unionAllowed.add(id));
  }

  if (unrestricted) return toPool || [];
  return (toPool || []).filter((hub) => unionAllowed.has(String(hub.id)));
}
